import * as React from 'react';
import { Logos } from '@draftkings/event-page-widget-contracts';
import { ParsedEvent } from '../../contracts/parsers/IParser';
import { TeamLogo } from './TeamLogo';
import { getTeamData } from './getTeamData';

import './index.scss';

type EventTeamsProps = {
    event: ParsedEvent;
    logosMap: Logos | undefined;
    staticS3Host: string;
    isTeamSwap: boolean;
    isSwooshTeamLogosEnabledForEventPage?: boolean;
    swooshBaseUrl: string;
};

export function EventTeams({
    event,
    logosMap,
    staticS3Host,
    isTeamSwap,
    isSwooshTeamLogosEnabledForEventPage,
    swooshBaseUrl
}: EventTeamsProps) {
    const team1LogoRef = React.useRef<HTMLImageElement>(null);
    const team2LogoRef = React.useRef<HTMLImageElement>(null);

    const [error, setError] = React.useState(false);
    const [loaded, setLoaded] = React.useState(false);
    const logosForLeague = logosMap ? logosMap[event.leagueId] : undefined;
    const { team1, team2 } = getTeamData(event.participants, isTeamSwap);
    const team1LogoUrl = logosForLeague?.Teams?.[team1.name];
    const team2LogoUrl = logosForLeague?.Teams?.[team2.name];
    const middleTerm = logosForLeague?.MiddleTerm?.toUpperCase() || '@';
    const hasTeamNames = team1.name && team2.name;
    const hasLogos = hasTeamNames && (isSwooshTeamLogosEnabledForEventPage || (team1LogoUrl && team2LogoUrl));
    const isOutright =
        !event.eventParticipantType ||
        event.eventParticipantType === 'MultiTeam' ||
        event.eventParticipantType === 'MultiPlayer';

    const onLoad = () => {
        if (team1LogoRef?.current?.complete && team2LogoRef?.current?.complete) {
            setLoaded(true);
        }
    };

    const onError = () => setError(true);

    React.useEffect(() => {
        onLoad();

        if (
            (team1LogoRef?.current?.complete && !team1LogoRef?.current?.naturalHeight) ||
            (team2LogoRef?.current?.complete && !team2LogoRef?.current?.naturalHeight)
        ) {
            setError(true);
        }
    }, [event, logosMap]);

    return !isOutright && hasLogos && !error ? (
        <div className="ep-event-teams__title" data-testid="ep-event-teams-title">
            {team1.name}
            <TeamLogo
                ref={team1LogoRef}
                logoUrl={team1LogoUrl}
                teamName={team1.name}
                staticS3Host={staticS3Host}
                loaded={loaded}
                onLoad={onLoad}
                onError={onError}
                participant={team1.participant}
                isSwooshTeamLogosEnabledForEventPage={isSwooshTeamLogosEnabledForEventPage}
                swooshBaseUrl={swooshBaseUrl}
            />
            <div className="ep-event-teams__divider">
                <div className="ep-event-teams__divider-box">{middleTerm}</div>
            </div>
            <TeamLogo
                ref={team2LogoRef}
                logoUrl={team2LogoUrl}
                teamName={team2.name}
                staticS3Host={staticS3Host}
                loaded={loaded}
                onLoad={onLoad}
                onError={onError}
                participant={team2.participant}
                isSwooshTeamLogosEnabledForEventPage={isSwooshTeamLogosEnabledForEventPage}
                swooshBaseUrl={swooshBaseUrl}
            />
            {team2.name}
        </div>
    ) : (
        <>{event.name}</>
    );
}
